import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, Mountain, BookOpen, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const rotatingQuotes = [
    "Nature did the filtering. We did the sourcing.",
    "Pressure made it perfect.",
    "Minerals matter. The rest is just liquid.",
    "Not all water earns its minerals.",
    "More than hydration. It's composition.",
    "Respect the source.",
    "Real minerals. Real difference.",
    "Not just pure. Right."
];

const AboutPage = () => {
    const { t, language } = useLanguage();
    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
    const [expandedValue, setExpandedValue] = useState(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentQuoteIndex((prev) => (prev + 1) % rotatingQuotes.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const values = [
        {
            id: 'sustainability',
            icon: Leaf,
            title: t('valueSustainability'),
            description: t('valueSustainabilityDesc'),
            detail: t('valueSustainabilityDetail')
        },
        {
            id: 'natural',
            icon: Mountain,
            title: t('valueNatural'),
            description: t('valueNaturalDesc'),
            detail: t('valueNaturalDetail')
        },
        {
            id: 'knowledge',
            icon: BookOpen,
            title: t('valueKnowledge'),
            description: t('valueKnowledgeDesc'),
            detail: t('valueKnowledgeDetail')
        }
    ];

    const toggleValue = (id) => {
        setExpandedValue(expandedValue === id ? null : id);
    };

    return (
        <div className="page-transition" data-testid="about-page">
            <SEO 
                title="Om oss"
                description="Raw Minerals er Norges første spesialiserte importør av premium mineralvann. Vi leter frem verdens fineste vann for bevisste norske ganer."
                canonical="/about"
            />
            {/* Hero */}
            <section className="py-20 md:py-32 bg-stone-50">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <p className="caption mb-4">{language === 'no' ? 'Om oss' : 'About us'}</p>
                        <h1 className="font-display text-5xl md:text-7xl font-light mb-12">
                            {t('ourStoryTitle')}
                        </h1>
                        <div className="text-lg text-stone-600 leading-relaxed">
                            {t('aboutIntro').split('\n\n').map((paragraph, index) => (
                                <p key={index} className="mb-8">{paragraph}</p>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>


            {/* Philosophy */}
            <section className="py-24 md:py-32 bg-fjord text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-6">
                            {t('ourPhilosophy')}
                        </p>
                        <div className="h-24 md:h-20 flex items-center justify-center">
                            <AnimatePresence mode="wait">
                                <motion.h2
                                    key={currentQuoteIndex}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    className="font-display text-2xl md:text-4xl font-light italic"
                                >
                                    "{rotatingQuotes[currentQuoteIndex]}"
                                </motion.h2>
                            </AnimatePresence>
                        </div>
                        <p className="text-white/70 leading-relaxed mt-8">
                            {t('philosophyDesc')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 md:py-32" data-testid="values-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <p className="caption mb-4">{t('whatWeStandFor')}</p>
                        <h2 className="font-display text-3xl md:text-4xl font-light">
                            {t('valuesGuide')}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {values.map((value, index) => {
                            const isExpanded = expandedValue === value.id;
                            const hasDetail = value.detail && value.detail.length > 0;
                            
                            return (
                                <motion.div
                                    key={value.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="text-center h-full"
                                >
                                    <div 
                                        className={`flex flex-col h-full ${hasDetail ? 'cursor-pointer group' : ''}`}
                                        onClick={() => hasDetail && toggleValue(value.id)}
                                        data-testid={`value-${value.id}`}
                                    >
                                        <value.icon className={`w-8 h-8 mx-auto mb-6 text-fjord transition-colors ${hasDetail ? 'group-hover:text-fjord/70' : ''}`} />
                                        <h3 className={`font-display text-lg mb-3 ${hasDetail ? 'group-hover:text-fjord transition-colors' : ''}`}>
                                            {value.title}
                                        </h3>
                                        <p className="text-sm text-stone-500 leading-relaxed flex-grow">
                                            {value.description}
                                        </p>
                                        {hasDetail && (
                                            <div className="mt-4 flex items-center justify-center gap-1 text-xs text-fjord/60 group-hover:text-fjord transition-colors">
                                                <span>{isExpanded ? t('clickToClose') : t('clickToReadMore')}</span>
                                                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Expanded Detail Panel */}
                    <AnimatePresence>
                        {expandedValue && values.find(v => v.id === expandedValue)?.detail && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.4, ease: 'easeInOut' }}
                                className="overflow-hidden"
                            >
                                <div className="max-w-3xl mx-auto mt-12 pt-12 border-t border-stone-200">
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: 0.1 }}
                                    >
                                        <div className="flex items-center justify-center gap-3 mb-8">
                                            {(() => {
                                                const activeValue = values.find(v => v.id === expandedValue);
                                                const IconComponent = activeValue?.icon;
                                                return IconComponent ? <IconComponent className="w-6 h-6 text-fjord" /> : null;
                                            })()}
                                            <h3 className="font-display text-2xl">
                                                {values.find(v => v.id === expandedValue)?.title}
                                            </h3>
                                        </div>
                                        <div className="text-stone-600 leading-relaxed">
                                            {values.find(v => v.id === expandedValue)?.detail.split('\n\n').map((paragraph, idx) => (
                                                <p key={idx} className="mb-6 last:mb-0">{paragraph}</p>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => setExpandedValue(null)}
                                            className="mt-8 mx-auto block text-sm text-fjord hover:text-fjord/70 transition-colors underline underline-offset-4"
                                        >
                                            {t('clickToClose')}
                                        </button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* Location */}
            <section className="py-24 md:py-32 bg-stone-50">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    {/* FAQ Section */}
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="text-center mb-12"
                        >
                            <p className="caption mb-4">{t('faqCaption')}</p>
                            <h2 className="font-display text-3xl md:text-4xl font-light">
                                {t('faqTitle')}
                            </h2>
                        </motion.div>

                        <div className="space-y-4">
                            {t('faqItems').map((item, index) => (
                                <motion.details
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                    viewport={{ once: true }}
                                    className="group border-b border-stone-200 pb-4"
                                >
                                    <summary className="flex justify-between items-center cursor-pointer list-none py-3 font-medium text-stone-800 hover:text-stone-600 transition-colors">
                                        <span>{item.q}</span>
                                        <span className="ml-4 flex-shrink-0 text-stone-400 group-open:rotate-45 transition-transform duration-200">+</span>
                                    </summary>
                                    <p className="text-stone-600 leading-relaxed pt-2 pb-4 pr-8">
                                        {item.a}
                                    </p>
                                </motion.details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
